import { auth } from '../api/auth'
import { ApiError } from '../api/client'

/**
 * Puente cookie → vr-app. Fuente única de la acción "Jugar": la usan el bridge
 * de `/` (`pages/PlayBridge.tsx`), el tab VR del bottom bar
 * (`components/AppNavBar.tsx`) y el CTA de `/data`.
 *
 * No hay gates acá: desde la decisión de producto de mg (2026-09-07, doc 32
 * §4) el jugador entra derecho al lobby del vr-app y los gates
 * (consentimiento → cuestionarios pendientes → sensores → chequeo previo)
 * corren adentro del vr-app, después de "Comenzar".
 *
 * Navega con `window.location` (no react-router): el vr-app es otro bundle
 * servido desde `/vr/`.
 *
 * ---------------------------------------------------------------------------
 * CÓMO VIAJA LA CREDENCIAL (cambio de 2026-09-08, vrs-docs/52 H-6 / D14)
 * ---------------------------------------------------------------------------
 * Antes: `POST /auth/exchange-token` devolvía el token PERMANENTE del jugador
 * (30 días) y esto navegaba a `/vr/?token=<token>`. Una query string no es un
 * canal privado: queda en el historial del navegador, se manda como `Referer`
 * al primer recurso de otro origen que pida la página, y nginx la loguea
 * entera (`deploy/nginx.conf` loguea `$request_uri`). El propio backend ya
 * rechazaba `?token=` para el WS del Quest (`routers/edge_bridge.py`).
 *
 * Ahora el exchange devuelve un TICKET efímero (60 s, un solo uso) y este
 * módulo lo pasa por **sessionStorage**, no por la URL:
 *
 *   - Es el único canal de los tres candidatos que NO deja rastro en ningún
 *     lado: no entra en el historial, no viaja en `Referer`, no llega al
 *     servidor y por lo tanto no se loguea. El fragmento `#` no llega al
 *     servidor pero SÍ entra en la entrada de historial (hay que borrarlo con
 *     `history.replaceState`, o sea que existe una ventana); un POST con
 *     redirect obliga a un form auto-submit y a que el vr-app se sirva por
 *     POST, que es mucha máquina para el mismo resultado.
 *   - `sessionStorage` es por origen y por pestaña: la MISMA pestaña que
 *     escribe es la que navega y lee, así que el handoff no se cruza con otra
 *     pestaña ni sobrevive al cierre.
 *   - Requiere mismo origen. En prod y en dev lo es (nginx sirve `/vr/` bajo
 *     el mismo host, y el dev server del web-app proxya `/vr` — ver
 *     `web-app/vite.config.ts`). Sólo si alguien apunta `VITE_VR_URL` a otro
 *     origen deja de servir, y ahí caemos al fragmento.
 *
 * El fallback por fragmento es seguro justamente porque lo que viaja ya no es
 * el token de larga vida: es un ticket que vale 60 s y una sola vez, y el
 * vr-app lo borra del historial apenas lo lee. Es también lo que usa el
 * callback de Google (`routers/auth.py`), donde un redirect del servidor no
 * puede escribir `sessionStorage`.
 */

/**
 * Clave de `sessionStorage` donde queda el ticket para el vr-app.
 *
 * ⚠️ ESPEJO: el consumidor es `vr-app/src/store/handoff.ts` (misma constante,
 * mismo literal). Los dos bundles son proyectos distintos del monorepo y no
 * comparten módulo, así que el literal está duplicado a propósito y cada lado
 * tiene un test que lo fija. Si cambia uno, cambia el otro.
 */
export const VR_HANDOFF_STORAGE_KEY = 'versports.auth.handoff'

/** Clave del fragmento en el fallback cross-origin. Mismo espejo. */
export const VR_HANDOFF_FRAGMENT_KEY = 'vrs_ticket'

/** `true` si `target` se resuelve al mismo origen que la página actual. */
export function isSameOrigin(target: string): boolean {
  try {
    return new URL(target, window.location.href).origin === window.location.origin
  } catch {
    return false
  }
}

/** Escribe el ticket donde el vr-app lo va a buscar. `false` si no se pudo
 *  (modo privado, cuota, storage deshabilitado) y hay que usar el fragmento. */
function stashTicket(ticket: string): boolean {
  try {
    window.sessionStorage.setItem(VR_HANDOFF_STORAGE_KEY, ticket)
    return true
  } catch {
    return false
  }
}

export async function openVrApp(page?: 'level-editor'): Promise<never> {
  const { ticket } = await auth.exchangeToken()
  const base = import.meta.env.VITE_VR_URL ?? '/vr/'
  const target = page ? new URL(page, new URL(base, window.location.href)).href : base
  const viaStorage = isSameOrigin(target) && stashTicket(ticket)
  window.location.href = viaStorage
    ? target
    : `${target}#${VR_HANDOFF_FRAGMENT_KEY}=${encodeURIComponent(ticket)}`
  // La navegación es asíncrona: dejamos la promesa pendiente para que el
  // caller no apague su estado de "abriendo…" mientras la página se va.
  return new Promise<never>(() => {})
}

/** Mensaje de error uniforme para los tres callers. */
export function vrBridgeError(e: unknown): string {
  return e instanceof ApiError ? e.message : 'No se pudo abrir el entrenamiento VR.'
}
